import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import Modal from './About.jsx';
import { Link, useNavigate } from 'react-router-dom';
import { TailSpin } from 'react-loading-icons';
import {
  createDraftPetPhotoDoc,
  onUploadPhotos,
  updatePetTypeInFirebase,
  updatePetQuestionnaireInFirebase,
  subscribeToPetPhotoDocument,
  startGenerationJob,
  startDailyVideoGeneration,
  completeFlowSignInWithApple,
  completeFlowSignInWithGoogle,
  consumePendingAuthRedirectResult,
  consumePendingEmailLinkSignIn,
  completePendingEmailLinkSignIn,
  completeFlowEmailPasswordAuth,
  sendEmailSignInLinkForFlow,
  signOutCurrentUser,
  onAuthUserChanged,
  getCurrentUser,
  hasResolvedInitialAuthState,
  waitForInitialAuthState,
  getUiBootstrapSnapshot,
  ensureUserProfileDocument,
  getUserProfile,
  subscribeToUserProfile,
  hasAuthoritativeHomeProfileData,
  canAccessProfileFromProfile,
  buildHomepageSnapshotFromProfile,
  cacheUserProfileSnapshot,
  cacheHomepageSnapshot,
  getAnalyticsEventContext,
  logAnalyticsEvent,
  shouldPreferMagicLinkEmailAuth,
} from './Firebase.js';
import AppHeader, { appHeaderActionClasses } from './AppHeader';

import PhotoUploader from './UploadPhotos/PhotoUploader.jsx'
import PetSelector from './UploadPhotos/PetSelector.jsx'
import AuthGateStep from './UploadPhotos/AuthGateStep.jsx'
import PetNameInput from './UploadPhotos/PetNameInput.jsx'
import PetChoiceQuestion from './UploadPhotos/PetChoiceQuestion.jsx'
import InstagramWall from './InstagramWall.jsx'
import LegalFooter from './LegalFooter.jsx';
import { getModernResultPath } from './resultPaths.js';
import { detectCurrentMobilePlatform } from './devicePlatform';

const FLUFFBALL_APP_STORE_URL = 'https://apps.apple.com/us/app/fluffball/id6761160687';

// Autoplaying, muted, looping video used for the landing-page demos.
const LoopVideo = ({ src, poster, className = '', style }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const node = videoRef.current;
    if (!node) {
      return;
    }
    node.muted = true;
    const playAttempt = node.play();
    if (playAttempt && typeof playAttempt.catch === 'function') {
      playAttempt.catch(() => {});
    }
  }, []);

  return (
    <video
      ref={videoRef}
      className={className}
      style={style}
      poster={poster}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      disablePictureInPicture
      disableRemotePlayback
    >
      <source src={src} type="video/mp4" />
    </video>
  );
};

// The three "watch them grow" stages shown under the hero.
const GROWTH_STAGES = [
  { src: '/app/newborn.mp4', poster: '/app/newborn.jpg', day: 'Day 1', label: 'Newborn' },
  { src: '/app/grow-3wk.mp4', poster: '/app/grow-3wk.jpg', day: 'Week 3', label: 'First wobbly steps' },
  { src: '/app/grow-6wk.mp4', poster: '/app/grow-6wk.jpg', day: 'Week 6', label: 'Full-speed zoomies' },
];

// const testimonials = [
//   { id: 1, name: "Paws P. 🐾", text: "Seeing my Max as a tiny pup again brought tears to my eyes. This app is a gem!" },
//   { id: 2, name: "Bella B. 🐶", text: "It's like a sweet throwback in time! Our puppy pics are now the talk of every pet parent meetup." },
//   { id: 3, name: "Whiskers W. 🐱", text: "Who knew Mr. Fluffy was such a cute kitten? This app brings back so many memories." },
//   { id: 4, name: "Fido F. 🦴", text: "From grown-up to puppy in seconds – I can't get enough of this app. It's paw-sitively amazing!" },
//   { id: 5, name: "Coco C. 🐾", text: "Adorable, nostalgic, and so much fun! Now I can see Coco as a puppy anytime I want." },
// ];

  const instagramPosts = [
    // Example posts
    {
      imageUrl: 'ig_photos/nutterbutterrescuepupper.png',
      caption: `Peanut woke up and said, "let's break the internet today!" I've always wondered what puppy Peanut looked like. I was told he was part of a hoarding case in California the first year of his life with a sister who didn't make it. He then somehow made it to Reno for 5 years before coming to me via the shelter as a medical surrender. Thank you Sam at @fluffball.app for these photos. 🥹❤️`,
      username: 'nutterbutterrescuepupper', 
      timestamp: '2023-11-26', 
      link: 'https://www.instagram.com/p/C0HbiTIJSF4/?img_index=1',
    },
    {
      imageUrl: 'ig_photos/piggiebully.png',
      caption: `Since we adopted Piggie at age 6, and we have no way to track down his history, we are so thankful for @fluffball.app for these AI renderings of Piggie as a puppy! I love that he even has his little white speckles on his chin and top of his nose. And of course…. His precious floppy ears.`,
      username: 'piggiebully', 
      timestamp: '2023-11-27', 
      link: 'https://www.instagram.com/p/C0J3ubNLGI3/?img_index=1',
    },
    {
      imageUrl: 'ig_photos/seniorprofile.png',
      caption: `I've always wanted to know what Henny looked like as a puppy, and @fluffball.app gave me that chance. AI technology made me my Henny bear as a CUTE WITTLE PUPPY 😭 #hennybear #adoptasenior #adoptdontshop #missyou🌈 #rainbowbridge #seniordog #rescueismyfavoritebreed #australianterrier`,
      username: 'seniorprofile', 
      timestamp: '2023-11-26', 
      link: 'https://www.instagram.com/p/C0Ikr0tOdVO/?img_index=2',
    },
    { 
      imageUrl: 'ig_photos/howlandwolfe.png',
      caption: `Mumma and Dad didn't get to see me as a little pupper and always tell me how sad it makes them. But thanks to @fluffball.app (and @hi.this.is.tatum!) they finally got to see de baby Howie!`,
      username: 'howlandwolfe', 
      timestamp: '2023-11-19', 
      link: 'https://www.instagram.com/p/Cz1UqjYLWfV/?hl=en&img_index=2',
    },
    { 
      imageUrl: 'ig_photos/wojpups.png',
      caption: `We have no idea what Otis looked like as a puppy, but our friends at @fluffball.app created this puppy photo of Otis.

What do you think? Do you think that's what Otis looked like as a puppy?`,
      username: 'wojpups', 
      timestamp: '2023-11-19', 
      link: 'https://www.instagram.com/p/Cz1kGg2Aeyj/?hl=en',
    },
    { 
      imageUrl: 'ig_photos/blindboyblaze.png',
      caption: `(Part 1) I caved and did the AI "see your dog as a puppy" and oh my goodness I am emotional! 😭😭 his body looks a little odd but hey, asking AI to do Blaze is a big ask 😂
These first few photos you can tell are built from the pictures I have from his adoption profile, which I put in there to compare. But wait til you see what they made from his portraits from @gonetothedogsphotography.....
(This magic was created using @fluffball.app)`,
      username: 'blindboyblaze', 
      timestamp: '2023-11-19', 
      link: 'https://www.instagram.com/p/Cz1LVKDuZTH/?hl=en',
    },
    { 
      imageUrl: 'ig_photos/tangled_up_in_peis.png',
      caption: `Thanks to @fluffball.app I have an idea of what my sweet Sargie might have looked like as a puppy. I may or may not have burst out into tears when I saw the photos ❤️❤️❤️ I always wonder what my old man would have been like when he was a puppy, no doubt he was just as sweet and cute as he is now. #imnotcryingyourecrying #sgtpeppertherescuepei #sweetsargie #fluffballapp`,
      username: 'tangled_up_in_peis', 
      timestamp: '2023-11-19', 
      link: 'https://www.instagram.com/p/Cz3p40DLA5O/?hl=en',
    },
    { 
      imageUrl: 'ig_photos/southerntofu__.png',
      caption: `Thanks to the @fluffball.app I now have somewhat of an idea of what my other half looked like as a puppy. It's crazy what AI can give us. I miss this guy daily and this was just something so special because I never got the pleasure of seeing him as a puppy. Thank you @fluffball.app`,
      username: 'southerntofu__', 
      timestamp: '2023-11-19', 
      link: 'https://www.instagram.com/p/Cz2aNB9rBt9/?hl=en&img_index=2',
    },
    { 
      imageUrl: 'ig_photos/chugsmeanmugs.png',
      caption: `We adopted Chugs at the age of 5 so we never got to see him as a puppy. Thanks to @fluffball.app we have a better idea of what he could have looked like. Oh how we wish you were a puppy with us, but still grateful you are in your furever home.🐶❤️`,
      username: 'chugsmeanmugs', 
      timestamp: '2023-11-20', 
      link: 'https://www.instagram.com/p/Cz2DQpAvvWr/?hl=en&img_index=2',
    },

    { 
      imageUrl: 'ig_photos/mydogmeatloaf.png',
      caption: `According to AI- here is what I looked like as a puppy @longislandbulldogrescue Thank you @fluffball.app for the great pics!!`,
      username: 'mydogmeatloaf', 
      timestamp: '2023-11-20', 
      link: 'https://www.instagram.com/p/Cz1_OJvPHQQ/?hl=en&img_index=5',
    },
    { 
      imageUrl: 'ig_photos/annelieseeee.png',
      caption: `I got an AI image of what Aurora may have looked like as a pup since I adopted her when she was 2 🥹 my sweet girl`,
      username: 'annelieseeee', 
      timestamp: '2023-11-19', 
      link: 'https://www.instagram.com/p/Cz1zwfbO9rY/?hl=en&img_index=2',
    },

  ];


const PET_PERSONALITY_OPTIONS = [
  { value: 'playful', label: 'Playful' },
  { value: 'sweet', label: 'Sweet' },
  { value: 'curious', label: 'Curious' },
  { value: 'calm', label: 'Calm' },
];

const PET_AGE_OPTIONS = [
  { value: 'under_2', label: 'Under 2' },
  { value: '2_to_5', label: '2-5' },
  { value: '6_to_9', label: '6-9' },
  { value: '10_plus', label: '10+' },
];

const PET_DESCRIPTOR_OPTIONS = [
  { value: 'cuddly', label: 'Cuddly' },
  { value: 'goofy', label: 'Goofy' },
  { value: 'independent', label: 'Independent' },
  { value: 'adventurous', label: 'Adventurous' },
];

const PET_FAVORITE_TRAIT_OPTIONS = [
  { value: 'sweet_face', label: 'Sweet face' },
  { value: 'big_personality', label: 'Big personality' },
  { value: 'gentle_energy', label: 'Gentle energy' },
  { value: 'silly_side', label: 'Silly side' },
];

const PET_FAVORITE_ACTIVITY_OPTIONS = [
  { value: 'playtime', label: 'Playtime' },
  { value: 'snuggling', label: 'Snuggling' },
  { value: 'exploring', label: 'Exploring' },
  { value: 'snack_hunting', label: 'Snack hunting' },
];

const PET_SOCIAL_STYLE_OPTIONS = [
  { value: 'loves_everyone', label: 'Loves everyone' },
  { value: 'warms_up_slowly', label: 'Warms up slowly' },
  { value: 'my_people_only', label: 'My people only' },
  { value: 'boss_of_the_room', label: 'Boss of the room' },
];

const PET_ENERGY_LEVEL_OPTIONS = [
  { value: 'mostly_mellow', label: 'Mostly mellow' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'high_energy', label: 'High energy' },
  { value: 'tiny_tornado', label: 'Tiny tornado' },
];

const PET_HAPPY_PLACE_OPTIONS = [
  { value: 'curled_up_home', label: 'Curled up at home' },
  { value: 'outside_adventure', label: 'Outside exploring' },
  { value: 'right_next_to_you', label: 'Right next to you' },
  { value: 'somewhere_exciting', label: 'Somewhere exciting' },
];

const PET_AFFECTION_LEVEL_OPTIONS = [
  { value: 'velcro_pet', label: 'Velcro-level attached' },
  { value: 'big_cuddler', label: 'Big cuddler' },
  { value: 'on_their_terms', label: 'On their terms' },
  { value: 'loves_you_nearby', label: 'Loves you nearby' },
];

const PET_CONFIDENCE_STYLE_OPTIONS = [
  { value: 'charges_right_in', label: 'Charges right in' },
  { value: 'watches_first', label: 'Watches first' },
  { value: 'needs_encouragement', label: 'Needs encouragement' },
  { value: 'seen_it_all', label: 'Acts like they have seen it all' },
];

const PET_VOCAL_STYLE_OPTIONS = [
  { value: 'talks_all_day', label: 'Talks all day' },
  { value: 'excited_commentator', label: 'Speaks up when excited' },
  { value: 'mostly_quiet', label: 'Mostly quiet' },
  { value: 'expressive_face', label: 'Says it with their face' },
];

const PET_TREAT_MOTIVATION_OPTIONS = [
  { value: 'anything_for_snacks', label: 'Would do anything for snacks' },
  { value: 'picky_but_interested', label: 'Likes treats, but has standards' },
  { value: 'prefers_play', label: 'Prefers play over food' },
  { value: 'not_treat_driven', label: 'Hardly cares about treats' },
];

const PET_PLAY_STYLE_OPTIONS = [
  { value: 'chasing_and_pouncing', label: 'Chasing and pouncing' },
  { value: 'wrestling_and_roughhousing', label: 'Wrestling and roughhousing' },
  { value: 'carrying_toys', label: 'Carrying toys around' },
  { value: 'quick_burst_then_done', label: 'Quick burst, then done' },
];

const PET_MISCHIEF_LEVEL_OPTIONS = [
  { value: 'total_troublemaker', label: 'Total troublemaker' },
  { value: 'sneaky_when_bored', label: 'Sneaky when bored' },
  { value: 'mostly_well_behaved', label: 'Mostly well-behaved' },
  { value: 'innocent_face_chaotic_mind', label: 'Innocent face, chaotic mind' },
];

const PET_ROUTINE_STYLE_OPTIONS = [
  { value: 'loves_a_schedule', label: 'Loves a schedule' },
  { value: 'easygoing_and_adaptable', label: 'Easygoing and adaptable' },
  { value: 'chaos_then_calm', label: 'Bursts of chaos, then calm' },
  { value: 'makes_own_rules', label: 'Makes their own rules' },
];

const getPetDisplayName = (petName) => String(petName || '').trim() || 'your pet';

const getLandingSocialSource = (flow, providerId) => {
  const normalizedFlow = String(flow || '').trim();
  const normalizedProviderId = String(providerId || '').trim().toLowerCase();
  const providerSuffix = normalizedProviderId === 'apple' ? 'apple' : 'google';

  if (normalizedFlow === 'landing_upload') {
    return `upload_continue_${providerSuffix}`;
  }

  return `home_header_sign_in_${providerSuffix}`;
};

const getLandingEmailSource = (flow) => {
  const normalizedFlow = String(flow || '').trim();
  if (normalizedFlow === 'landing_upload') {
    return 'upload_continue_email_link';
  }

  return 'home_header_email_link';
};

const AUTH_GATE_PAGE = 3;
const PET_NAME_PAGE = 4;
const QUESTION_FLOW_START_PAGE = 5;

const QUESTIONNAIRE_STEPS = [
  {
    key: 'petPersonality',
    analyticsEvent: 'fluffball-pet-personality-selected',
    analyticsProperty: 'pet_personality',
    options: PET_PERSONALITY_OPTIONS,
    title: (petDisplayName) => `What is ${petDisplayName}'s personality like?`,
  },
  {
    key: 'petAgeBucket',
    analyticsEvent: 'fluffball-pet-age-selected',
    analyticsProperty: 'pet_age_bucket',
    options: PET_AGE_OPTIONS,
    title: (petDisplayName) => `How old is ${petDisplayName} now?`,
  },
  {
    key: 'petDescriptor',
    analyticsEvent: 'fluffball-pet-descriptor-selected',
    analyticsProperty: 'pet_descriptor',
    options: PET_DESCRIPTOR_OPTIONS,
    title: (petDisplayName) => `What best describes ${petDisplayName}?`,
  },
  {
    key: 'petFavoriteTrait',
    analyticsEvent: 'fluffball-pet-favorite-trait-selected',
    analyticsProperty: 'pet_favorite_trait',
    options: PET_FAVORITE_TRAIT_OPTIONS,
    title: (petDisplayName) => `What do you love most about ${petDisplayName}?`,
  },
  {
    key: 'petFavoriteActivity',
    analyticsEvent: 'fluffball-pet-favorite-activity-selected',
    analyticsProperty: 'pet_favorite_activity',
    options: PET_FAVORITE_ACTIVITY_OPTIONS,
    title: (petDisplayName) => `What does ${petDisplayName} love doing most?`,
  },
  {
    key: 'petSocialStyle',
    analyticsEvent: 'fluffball-pet-social-style-selected',
    analyticsProperty: 'pet_social_style',
    options: PET_SOCIAL_STYLE_OPTIONS,
    title: (petDisplayName) => `How does ${petDisplayName} act around new people or pets?`,
  },
  {
    key: 'petEnergyLevel',
    analyticsEvent: 'fluffball-pet-energy-level-selected',
    analyticsProperty: 'pet_energy_level',
    options: PET_ENERGY_LEVEL_OPTIONS,
    title: (petDisplayName) => `How energetic is ${petDisplayName} most days?`,
  },
  {
    key: 'petHappyPlace',
    analyticsEvent: 'fluffball-pet-happy-place-selected',
    analyticsProperty: 'pet_happy_place',
    options: PET_HAPPY_PLACE_OPTIONS,
    title: (petDisplayName) => `Where does ${petDisplayName} seem happiest?`,
  },
  {
    key: 'petAffectionLevel',
    analyticsEvent: 'fluffball-pet-affection-level-selected',
    analyticsProperty: 'pet_affection_level',
    options: PET_AFFECTION_LEVEL_OPTIONS,
    title: (petDisplayName) => `How affectionate is ${petDisplayName}?`,
  },
  {
    key: 'petConfidenceStyle',
    analyticsEvent: 'fluffball-pet-confidence-style-selected',
    analyticsProperty: 'pet_confidence_style',
    options: PET_CONFIDENCE_STYLE_OPTIONS,
    title: (petDisplayName) => `How does ${petDisplayName} handle something new?`,
  },
  {
    key: 'petVocalStyle',
    analyticsEvent: 'fluffball-pet-vocal-style-selected',
    analyticsProperty: 'pet_vocal_style',
    options: PET_VOCAL_STYLE_OPTIONS,
    title: (petDisplayName) => `How chatty is ${petDisplayName}?`,
  },
  {
    key: 'petTreatMotivation',
    analyticsEvent: 'fluffball-pet-treat-motivation-selected',
    analyticsProperty: 'pet_treat_motivation',
    options: PET_TREAT_MOTIVATION_OPTIONS,
    title: (petDisplayName) => `How motivated is ${petDisplayName} by treats?`,
  },
  {
    key: 'petPlayStyle',
    analyticsEvent: 'fluffball-pet-play-style-selected',
    analyticsProperty: 'pet_play_style',
    options: PET_PLAY_STYLE_OPTIONS,
    title: (petDisplayName) => `What kind of play does ${petDisplayName} love most?`,
  },
  {
    key: 'petMischiefLevel',
    analyticsEvent: 'fluffball-pet-mischief-level-selected',
    analyticsProperty: 'pet_mischief_level',
    options: PET_MISCHIEF_LEVEL_OPTIONS,
    title: (petDisplayName) => `How mischievous is ${petDisplayName}?`,
  },
  {
    key: 'petRoutineStyle',
    analyticsEvent: 'fluffball-pet-routine-style-selected',
    analyticsProperty: 'pet_routine_style',
    options: PET_ROUTINE_STYLE_OPTIONS,
    title: (petDisplayName) => `What kind of routine fits ${petDisplayName} best?`,
  },
];

// const Testimonial = ({ testimonial }) => (
//   <div className="bg-blue-700 p-4 rounded-lg">
//     <p className="text-sm font-bold sm:text-lg">{testimonial.text}</p>
//     <p className="text-xs sm:text-sm text-white mt-2">- {testimonial.name}</p>
//   </div>
// );

const LandingPage = () => {
  const navigate = useNavigate();
  const mobilePlatform = detectCurrentMobilePlatform();
  const isIOSMobile = mobilePlatform === 'ios';
  const [bootstrapSnapshot] = useState(() => getUiBootstrapSnapshot());
  const initialCurrentUser = getCurrentUser();
  const initialSignedInUser = initialCurrentUser && !initialCurrentUser.isAnonymous ? initialCurrentUser : null;
  const [getStartedModalOpen, setGetStartedModalOpen] = useState(false);
  const [authUser, setAuthUser] = useState(() => bootstrapSnapshot.authUser || initialSignedInUser);
  const [authLoading, setAuthLoading] = useState(
    () => !initialSignedInUser
  );
  const [initialAuthResolved, setInitialAuthResolved] = useState(
    () => Boolean(bootstrapSnapshot.authUser) || hasResolvedInitialAuthState()
  );
  const [authError, setAuthError] = useState('');
  const [authInfo, setAuthInfo] = useState('');
  const [userProfile, setUserProfile] = useState(() => bootstrapSnapshot.userProfile);
  const [userProfileResolved, setUserProfileResolved] = useState(
    () => !bootstrapSnapshot.authUser || bootstrapSnapshot.hasCachedProfile
  );
  const [homeHeroReady, setHomeHeroReady] = useState(
    () => Boolean(bootstrapSnapshot.authUser) && hasAuthoritativeHomeProfileData(bootstrapSnapshot.userProfile)
  );
  const [homeHeroState, setHomeHeroState] = useState(() => (
    buildHomepageSnapshotFromProfile(
      bootstrapSnapshot.authUser,
      bootstrapSnapshot.userProfile,
      bootstrapSnapshot.homepage
    ).heroState
  ));
  const [homeActionDocId, setHomeActionDocId] = useState(() => (
    buildHomepageSnapshotFromProfile(
      bootstrapSnapshot.authUser,
      bootstrapSnapshot.userProfile,
      bootstrapSnapshot.homepage
    ).homeActionDocId
  ));
  const [latestResultDocId, setLatestResultDocId] = useState(
    () => (
      buildHomepageSnapshotFromProfile(
        bootstrapSnapshot.authUser,
        bootstrapSnapshot.userProfile,
        bootstrapSnapshot.homepage
      ).latestResultDocId
    )
  );
  const [latestPrimaryResultDocId, setLatestPrimaryResultDocId] = useState(
    () => String(bootstrapSnapshot.userProfile?.latest_primary_result_doc_id || '').trim()
  );
  const [isStartingDailyVideo, setIsStartingDailyVideo] = useState(false);
  const [homeStatusMessage, setHomeStatusMessage] = useState('');
  const initialHeroStateRef = useRef(
    buildHomepageSnapshotFromProfile(
      bootstrapSnapshot.authUser,
      bootstrapSnapshot.userProfile,
      bootstrapSnapshot.homepage
    ).heroState
  );
  const initialHeroReadyRef = useRef(
    Boolean(bootstrapSnapshot.authUser) && hasAuthoritativeHomeProfileData(bootstrapSnapshot.userProfile)
  );
  const homePageViewLoggedRef = useRef(false);


  const [selectedFiles, setSelectedFiles] = useState([]);
  const [modalPage, setModalPage] = useState(1);
  const [people, setPeople] = useState(null)
  const [petType, setPetType] = useState(null)
  const [petName, setPetName] = useState('')
  const [petPersonality, setPetPersonality] = useState('');
  const [petAgeBucket, setPetAgeBucket] = useState('');
  const [petDescriptor, setPetDescriptor] = useState('');
  const [petFavoriteTrait, setPetFavoriteTrait] = useState('');
  const [petFavoriteActivity, setPetFavoriteActivity] = useState('');
  const [petSocialStyle, setPetSocialStyle] = useState('');
  const [petEnergyLevel, setPetEnergyLevel] = useState('');
  const [petHappyPlace, setPetHappyPlace] = useState('');
  const [petAffectionLevel, setPetAffectionLevel] = useState('');
  const [petConfidenceStyle, setPetConfidenceStyle] = useState('');
  const [petVocalStyle, setPetVocalStyle] = useState('');
  const [petTreatMotivation, setPetTreatMotivation] = useState('');
  const [petPlayStyle, setPetPlayStyle] = useState('');
  const [petMischiefLevel, setPetMischiefLevel] = useState('');
  const [petRoutineStyle, setPetRoutineStyle] = useState('');
  const [uploadedDocId, setUploadedDocId] = useState('');
  const [utmParams, setUTMParams] = useState({});
  const [isTransitioningToResult, setIsTransitioningToResult] = useState(false);
  const [isCompletingFlowAuth, setIsCompletingFlowAuth] = useState(false);
  const [uploadEmailLinkPending, setUploadEmailLinkPending] = useState(false);
  const [isPreparingPostAuthGeneration, setIsPreparingPostAuthGeneration] = useState(false);
  const [headerAuthModalOpen, setHeaderAuthModalOpen] = useState(false);
  const [headerAuthError, setHeaderAuthError] = useState('');
  const [headerAuthInfo, setHeaderAuthInfo] = useState('');
  const [isCompletingHeaderAuth, setIsCompletingHeaderAuth] = useState(false);
  const [headerEmailLinkPending, setHeaderEmailLinkPending] = useState(false);
  const [modalHeaderSnapshot, setModalHeaderSnapshot] = useState({
    isSignedIn: Boolean(bootstrapSnapshot.authUser || initialSignedInUser),
    canAccessProfile: canAccessProfileFromProfile(bootstrapSnapshot.userProfile),
  });
  const wasModalOpenRef = useRef(false);
  const pendingDraftCreationTokenRef = useRef(0);
  const headerAuthPromptLoggedRef = useRef(false);
  const uploadAuthPromptLoggedRef = useRef('');
  const flowStepViewLoggedRef = useRef('');
  const authRedirectHandledRef = useRef(false);
  const prefersMagicLinkEmailAuth = shouldPreferMagicLinkEmailAuth();

  const getUTMParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || '',
      // Add other UTM parameters as needed
    };
  };  

  const trackLandingEvent = useCallback((eventName, properties = {}, userOverride = null) => {
    const analyticsContext = getAnalyticsEventContext(userOverride || authUser);
    logAnalyticsEvent({
      analyticsContext,
      eventName,
      properties: {
        screen: 'home',
        ...properties,
      },
    });
  }, [authUser]);

  const handleAppStoreClick = useCallback((source = 'unknown') => {
    trackLandingEvent('fluffball-app-store-clicked', {
      source,
      destination: 'app_store',
      cta_label: 'Get the app',
    });

    window.setTimeout(() => {
      window.location.href = FLUFFBALL_APP_STORE_URL;
    }, 50);
  }, [trackLandingEvent]);

  useEffect(() => {
    if (initialAuthResolved) {
      return () => {};
    }

    let isMounted = true;
    waitForInitialAuthState().then((resolvedUser) => {
      if (!isMounted) {
        return;
      }
      setInitialAuthResolved(true);
    });

    return () => {
      isMounted = false;
    };
  }, [initialAuthResolved]);

  useEffect(() => {
    if (authLoading || homePageViewLoggedRef.current) {
      return;
    }

    trackLandingEvent('fluffball-home-viewed', {
      signed_in: Boolean(authUser),
    });
    homePageViewLoggedRef.current = true;
  }, [authLoading, authUser, trackLandingEvent]);

  useEffect(() => {
    setUTMParams(getUTMParams());
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthUserChanged((user) => {
      setInitialAuthResolved(true);
      setAuthUser(user && !user.isAnonymous ? user : null);
      setAuthLoading(false);
      if (user && !user.isAnonymous) {
        ensureUserProfileDocument(user).catch((profileError) => {
          console.error('Failed to ensure user profile document:', profileError);
        });
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (authLoading) {
      return () => {};
    }

    if (!authUser) {
      setUserProfile(null);
      setUserProfileResolved(true);
      setHomeHeroReady(true);
      setHomeHeroState('guest');
      setHomeActionDocId('');
      setLatestResultDocId('');
      setLatestPrimaryResultDocId('');
      return () => {};
    }

    setUserProfileResolved(false);
    const cachedProfileIsAuthoritative = hasAuthoritativeHomeProfileData(userProfile);
    setHomeHeroReady(cachedProfileIsAuthoritative);
    return subscribeToUserProfile(authUser.uid, (profileData) => {
      const nextProfileData = profileData || null;
      const nextHomepageSnapshot = buildHomepageSnapshotFromProfile(
        authUser,
        nextProfileData,
        bootstrapSnapshot.homepage
      );
      setUserProfile(nextProfileData);
      setUserProfileResolved(true);
      setHomeHeroReady(true);
      setHomeHeroState(nextHomepageSnapshot.heroState);
      setHomeActionDocId(nextHomepageSnapshot.homeActionDocId || '');
      setLatestResultDocId(nextHomepageSnapshot.latestResultDocId || '');
      setLatestPrimaryResultDocId(String(nextProfileData?.latest_primary_result_doc_id || '').trim());
      cacheUserProfileSnapshot(authUser, nextProfileData);
      cacheHomepageSnapshot(authUser, nextHomepageSnapshot);
    });
  }, [authLoading, authUser, bootstrapSnapshot.homepage, userProfile]);

  const handleFileChange = (event) => {
    if (event.target.files.length > 20) {
      alert("You can only upload up to 20 images.");
      return;
    }
    setSelectedFiles([...event.target.files]);
  };

  const handleCloseGetStartedModal = () => {
    trackLandingEvent('fluffball-onboarding-closed', {
      modal_page: modalPage,
      pet_type: petType || '',
    });
    setGetStartedModalOpen(false);
    setModalPage(1);
    setPetType(null);
    setPetName('');
    setPetPersonality('');
    setPetAgeBucket('');
    setPetDescriptor('');
    setPetFavoriteTrait('');
    setPetFavoriteActivity('');
    setPetSocialStyle('');
    setPetEnergyLevel('');
    setPetHappyPlace('');
    setPetAffectionLevel('');
    setPetConfidenceStyle('');
    setPetVocalStyle('');
    setPetTreatMotivation('');
    setPetPlayStyle('');
    setPetMischiefLevel('');
    setPetRoutineStyle('');
    setUploadedDocId('');
    setIsTransitioningToResult(false);
    setIsCompletingFlowAuth(false);
    setUploadEmailLinkPending(false);
    setIsPreparingPostAuthGeneration(false);
    setAuthError('');
    setAuthInfo('');
    pendingDraftCreationTokenRef.current += 1;
  };

  const handleCloseHeaderAuthModal = () => {
    setHeaderAuthModalOpen(false);
    setHeaderAuthError('');
    setHeaderAuthInfo('');
    setIsCompletingHeaderAuth(false);
    setHeaderEmailLinkPending(false);
  };

  const handleSignOut = async () => {
    try {
      trackLandingEvent('fluffball-sign-out-clicked', {
        source: 'home',
      });
      await signOutCurrentUser();
      setAuthUser(null);
      setGetStartedModalOpen(false);
      navigate('/', { replace: true });
    } catch (error) {
      console.error("Error signing out:", error);
      setAuthError("Could not sign out right now. Please try again.");
    }
  };
  const canCreateDailyFromHome = homeHeroState === 'daily_available';
  const canOpenDailyFromHome = homeHeroState === 'daily_generating' || homeHeroState === 'daily_ready';
  const canAccessProfile = canAccessProfileFromProfile(userProfile);
  const headerIsSignedIn = getStartedModalOpen ? modalHeaderSnapshot.isSignedIn : Boolean(authUser);
  const headerCanAccessProfile = getStartedModalOpen
    ? modalHeaderSnapshot.canAccessProfile
    : canAccessProfile;

  useEffect(() => {
    if (getStartedModalOpen && !wasModalOpenRef.current) {
      setModalHeaderSnapshot({
        isSignedIn: Boolean(authUser),
        canAccessProfile,
      });
    }

    wasModalOpenRef.current = getStartedModalOpen;
  }, [authUser, canAccessProfile, getStartedModalOpen]);

  useEffect(() => {
    if (!getStartedModalOpen) {
      flowStepViewLoggedRef.current = '';
      return;
    }

    let eventName = '';
    let eventKey = '';
    let eventProperties = {};

    if (modalPage === 1) {
      eventName = 'fluffball-pet-type-screen-viewed';
      eventKey = 'pet_type';
      eventProperties = {};
    } else if (modalPage === 2) {
      eventName = 'fluffball-upload-screen-viewed';
      eventKey = 'upload';
      eventProperties = {
        pet_type: petType || '',
        doc_id: String(uploadedDocId || '').trim(),
      };
    } else if (modalPage === PET_NAME_PAGE) {
      eventName = 'fluffball-pet-name-viewed';
      eventKey = 'pet_name';
      eventProperties = {
        pet_type: petType || '',
        doc_id: String(uploadedDocId || '').trim(),
      };
    } else if (
      modalPage >= QUESTION_FLOW_START_PAGE
      && modalPage < QUESTION_FLOW_START_PAGE + QUESTIONNAIRE_STEPS.length
    ) {
      const stepConfig = QUESTIONNAIRE_STEPS[modalPage - QUESTION_FLOW_START_PAGE];
      if (stepConfig) {
        eventName = 'fluffball-question-step-viewed';
        eventKey = `question:${stepConfig.key}`;
        eventProperties = {
          pet_type: petType || '',
          pet_name: petName || '',
          doc_id: String(uploadedDocId || '').trim(),
          question_key: stepConfig.key,
        };
      }
    }

    if (!eventName || !eventKey || flowStepViewLoggedRef.current === eventKey) {
      return;
    }

    trackLandingEvent(eventName, eventProperties);
    flowStepViewLoggedRef.current = eventKey;
  }, [getStartedModalOpen, modalPage, petName, petType, trackLandingEvent, uploadedDocId]);

  useEffect(() => {
    if (!headerAuthModalOpen) {
      headerAuthPromptLoggedRef.current = false;
      return;
    }

    if (headerAuthPromptLoggedRef.current) {
      return;
    }

    trackLandingEvent('fluffball-auth-prompt-viewed', {
      source: 'home_header_sign_in',
    });
    headerAuthPromptLoggedRef.current = true;
  }, [headerAuthModalOpen, trackLandingEvent]);

  useEffect(() => {
    const trimmedDocId = String(uploadedDocId || '').trim();
    if (!getStartedModalOpen || modalPage !== AUTH_GATE_PAGE || !trimmedDocId) {
      uploadAuthPromptLoggedRef.current = '';
      return;
    }

    const promptKey = `${trimmedDocId}:${petType || ''}`;
    if (uploadAuthPromptLoggedRef.current === promptKey) {
      return;
    }

    trackLandingEvent('fluffball-auth-prompt-viewed', {
      source: 'upload_continue',
      doc_id: trimmedDocId,
      pet_type: petType || '',
    });
    uploadAuthPromptLoggedRef.current = promptKey;
  }, [getStartedModalOpen, modalPage, petType, trackLandingEvent, uploadedDocId]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (authUser && !userProfileResolved) {
      return;
    }

    if (authUser) {
      const nextHomepageSnapshot = buildHomepageSnapshotFromProfile(authUser, userProfile, bootstrapSnapshot.homepage);
      setHomeHeroState(nextHomepageSnapshot.heroState);
      setHomeActionDocId(nextHomepageSnapshot.homeActionDocId || '');
      setLatestResultDocId(nextHomepageSnapshot.latestResultDocId || '');
      setLatestPrimaryResultDocId(String(userProfile?.latest_primary_result_doc_id || '').trim());
      cacheHomepageSnapshot(authUser, nextHomepageSnapshot);
    }
  }, [
    authLoading,
    authUser,
    bootstrapSnapshot.homepage,
    userProfileResolved,
    userProfile,
  ]);

  const handleStartDailyVideoFromHome = useCallback(async ({
    userOverride = null,
    profileOverride,
    latestPrimaryResultDocIdOverride = '',
    latestResultDocIdOverride = '',
  } = {}) => {
    const activeUser = userOverride || authUser;
    const activeProfile = typeof profileOverride === 'undefined' ? userProfile : profileOverride;
    const activeLatestPrimaryResultDocId = String(
      latestPrimaryResultDocIdOverride ||
      activeProfile?.latest_primary_result_doc_id ||
      latestPrimaryResultDocId ||
      ''
    ).trim();
    const activeLatestResultDocId = String(
      latestResultDocIdOverride ||
      activeProfile?.latest_result_doc_id ||
      latestResultDocId ||
      ''
    ).trim();

    if (!activeUser || isStartingDailyVideo) {
      return;
    }

    if (authLoading) {
      setHomeStatusMessage('Finishing sign-in. Please try again in a second.');
      return;
    }

    setHomeStatusMessage('');
    setAuthError('');
    setIsStartingDailyVideo(true);

    try {
      const fallbackResultId =
        Array.isArray(activeProfile?.result_doc_ids) && activeProfile.result_doc_ids.length > 0
          ? String(activeProfile.result_doc_ids[0] || '').trim()
          : '';
      const sourceDocId = activeLatestPrimaryResultDocId || activeLatestResultDocId || fallbackResultId;

      if (!sourceDocId) {
        throw new Error('Missing source document for daily video generation');
      }

      const response = await startDailyVideoGeneration({
        userUid: activeUser.uid,
        sourceDocId,
      });
      const nextDocId = String(response?.doc_id || '').trim();
      if (!nextDocId) {
        throw new Error('Missing doc_id in daily generation response');
      }
      navigate(getModernResultPath(nextDocId));
    } catch (dailyError) {
      console.error('Failed to start daily video from homepage:', dailyError);
      setHomeStatusMessage('Could not start your daily video right now. Please try again.');
    } finally {
      setIsStartingDailyVideo(false);
    }
  }, [authLoading, authUser, isStartingDailyVideo, latestPrimaryResultDocId, latestResultDocId, navigate, userProfile]);

  const routeSignedInUserToNextStep = useCallback(async (
    userArg = null,
    profileArg,
    options = {}
  ) => {
    const {
      preferProfileForExistingAccount = false,
    } = options;
    const nextUser = userArg || authUser || getCurrentUser();
    if (!nextUser || nextUser.isAnonymous) {
      return false;
    }

    let nextProfile = typeof profileArg === 'undefined' ? userProfile : (profileArg || null);
    const shouldFetchProfile = (
      !nextProfile ||
      String(nextProfile?.uid || '').trim() !== nextUser.uid ||
      !hasAuthoritativeHomeProfileData(nextProfile)
    );

    if (shouldFetchProfile) {
      try {
        await ensureUserProfileDocument(nextUser);
        nextProfile = await getUserProfile(nextUser.uid);
      } catch (profileError) {
        console.error('Failed to load user profile for homepage routing:', profileError);
      }
    }

    const nextHomepageSnapshot = buildHomepageSnapshotFromProfile(
      nextUser,
      nextProfile,
      bootstrapSnapshot.homepage
    );
    const nextLatestPrimaryResultDocId = String(nextProfile?.latest_primary_result_doc_id || '').trim();
    const hasExistingResult = Boolean(
      nextHomepageSnapshot.homeActionDocId ||
      nextHomepageSnapshot.latestResultDocId ||
      nextLatestPrimaryResultDocId ||
      (Array.isArray(nextProfile?.result_doc_ids) && nextProfile.result_doc_ids.some((item) => typeof item === 'string' && item.trim()))
    );

    setAuthUser(nextUser);
    setUserProfile(nextProfile);
    setUserProfileResolved(true);
    setHomeHeroReady(hasAuthoritativeHomeProfileData(nextProfile));
    setHomeHeroState(nextHomepageSnapshot.heroState);
    setHomeActionDocId(nextHomepageSnapshot.homeActionDocId || '');
    setLatestResultDocId(nextHomepageSnapshot.latestResultDocId || '');
    setLatestPrimaryResultDocId(nextLatestPrimaryResultDocId);
    cacheUserProfileSnapshot(nextUser, nextProfile);
    cacheHomepageSnapshot(nextUser, nextHomepageSnapshot);

    if (preferProfileForExistingAccount && hasExistingResult) {
      setGetStartedModalOpen(false);
      navigate('/profile');
      return true;
    }

    if (nextHomepageSnapshot.heroState === 'daily_available') {
      setGetStartedModalOpen(false);
      await handleStartDailyVideoFromHome({
        userOverride: nextUser,
        profileOverride: nextProfile,
        latestPrimaryResultDocIdOverride: nextLatestPrimaryResultDocId,
        latestResultDocIdOverride: nextHomepageSnapshot.latestResultDocId,
      });
      return true;
    }

    if (nextHomepageSnapshot.heroState === 'daily_generating' || nextHomepageSnapshot.heroState === 'daily_ready') {
      setGetStartedModalOpen(false);
      navigate(
        nextHomepageSnapshot.homeActionDocId
          ? getModernResultPath(nextHomepageSnapshot.homeActionDocId)
          : '/profile'
      );
      return true;
    }

    if (hasExistingResult) {
      setGetStartedModalOpen(false);
      navigate('/profile');
      return true;
    }

    return false;
  }, [
    authUser,
    bootstrapSnapshot.homepage,
    handleStartDailyVideoFromHome,
    navigate,
    userProfile,
  ]);

  const waitForGenerationToBegin = useCallback((docIdValue) => new Promise((resolve, reject) => {
    const trimmedDocId = String(docIdValue || '').trim();
    if (!trimmedDocId) {
      resolve();
      return;
    }

    let settled = false;
    let unsubscribe = null;
    const timeoutId = setTimeout(() => {
      if (settled) {
        return;
      }
      settled = true;
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
      reject(new Error('Could not start your generation right now. Please try again.'));
    }, 30000);

    const finish = (callback, value) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
      callback(value);
    };

    unsubscribe = subscribeToPetPhotoDocument(trimmedDocId, (resultDoc) => {
      const generationStatus = String(resultDoc?.generation_status || '').trim().toLowerCase();
      if (
        generationStatus === 'generating_images'
        || generationStatus === 'generating_videos'
        || generationStatus === 'completed'
      ) {
        finish(resolve);
        return;
      }

      if (generationStatus === 'failed') {
        finish(
          reject,
          new Error(String(resultDoc?.generation_error || 'Could not start your generation right now. Please try again.'))
        );
      }
    });
  }), []);

  const waitForFirstResultSnapshot = useCallback((docIdValue) => new Promise((resolve, reject) => {
    const trimmedDocId = String(docIdValue || '').trim();
    if (!trimmedDocId) {
      reject(new Error('Missing result document.'));
      return;
    }

    let settled = false;
    let unsubscribe = null;
    const timeoutId = setTimeout(() => {
      if (settled) {
        return;
      }
      settled = true;
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
      reject(new Error('Could not load your result right now. Please try again.'));
    }, 15000);

    const finish = (callback, value) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
      callback(value);
    };

    unsubscribe = subscribeToPetPhotoDocument(trimmedDocId, (resultDoc) => {
      if (!resultDoc) {
        return;
      }
      finish(resolve, resultDoc);
    });
  }), []);

  const beginGenerationForDoc = useCallback(async (
    docIdValue,
    petNameValue = '',
    { waitForGenerationStart = true, petTypeOverride } = {},
  ) => {
    const trimmedDocId = String(docIdValue || '').trim();
    const resolvedPetType = String(
      typeof petTypeOverride === 'string' && petTypeOverride.trim()
        ? petTypeOverride
        : (petType || '')
    ).trim();
    if (!trimmedDocId) {
      throw new Error('Missing generation document.');
    }

    await updatePetTypeInFirebase(trimmedDocId, resolvedPetType, utmParams, petNameValue);
    await startGenerationJob(trimmedDocId, true);
    trackLandingEvent('fluffball-generation-started', {
      doc_id: trimmedDocId,
      pet_type: resolvedPetType,
      pet_name: petNameValue || '',
    });
    setUploadedDocId(trimmedDocId);
    if (waitForGenerationStart) {
      await waitForGenerationToBegin(trimmedDocId);
    }
  }, [petType, trackLandingEvent, utmParams, waitForGenerationToBegin]);

  const handleOpenGetStartedModal = async () => {
    if (isIOSMobile) {
      handleAppStoreClick('onboarding_entry');
      return;
    }

    if (authLoading && authUser) {
      setAuthError('Finishing sign-in. Please try again in a second.');
      return;
    }

    if (authUser) {
      const routedExistingUser = await routeSignedInUserToNextStep(authUser, undefined, {
        preferProfileForExistingAccount: true,
      });
      if (routedExistingUser) {
        return;
      }
    }

    setAuthError('');
    setAuthInfo('');
    setUploadEmailLinkPending(false);
    trackLandingEvent('fluffball-onboarding-started', {
      source: authUser ? 'home_signed_in' : 'home_guest',
    }, authUser);
    setGetStartedModalOpen(true);
  };

  const handlePrimaryHeroAction = async (source = 'hero') => {
    if (isIOSMobile) {
      handleAppStoreClick(source);
      return;
    }

    trackLandingEvent('fluffball-get-started-clicked', {
      signed_in: Boolean(authUser),
    });
    let resolvedAuthUser = authUser;
    if (authLoading && !initialAuthResolved) {
      const nextAuthUser = await waitForInitialAuthState();
      resolvedAuthUser = nextAuthUser && !nextAuthUser.isAnonymous ? nextAuthUser : null;
      setInitialAuthResolved(true);
      setAuthLoading(false);
      setAuthUser(resolvedAuthUser);
    }

    if (resolvedAuthUser) {
      const routedExistingUser = await routeSignedInUserToNextStep(resolvedAuthUser, undefined, {
        preferProfileForExistingAccount: true,
      });
      if (routedExistingUser) {
        return;
      }
    }
    if (canCreateDailyFromHome) {
      await handleStartDailyVideoFromHome();
      return;
    }
    if (canOpenDailyFromHome) {
      navigate(homeActionDocId ? getModernResultPath(homeActionDocId) : '/profile');
      return;
    }
    await handleOpenGetStartedModal();
  };

  const handleHeaderSignIn = async () => {
    setHeaderAuthError('');
    setHeaderAuthInfo('');
    setHeaderEmailLinkPending(false);
    trackLandingEvent('fluffball-sign-in-clicked', {
      source: 'home_header_sign_in',
    });
    setHeaderAuthModalOpen(true);
  };

  const handleHeaderAuthSuccess = useCallback(async (user, source) => {
    setAuthUser(user);
    trackLandingEvent('fluffball-sign-in-succeeded', { source }, user);
    handleCloseHeaderAuthModal();
    await routeSignedInUserToNextStep(user, undefined, {
      preferProfileForExistingAccount: true,
    });
  }, [routeSignedInUserToNextStep, trackLandingEvent]);

  const finalizeUploadAuthAndStartGeneration = useCallback(async ({
    user,
    source,
    docIdValue,
    petTypeOverride = '',
  }) => {
    const trimmedDocId = String(docIdValue || '').trim();
    const resolvedPetType = String(petTypeOverride || petType || '').trim();

    if (!trimmedDocId) {
      setAuthError('Missing generation document. Please try again.');
      return;
    }

    setGetStartedModalOpen(true);
    setUploadedDocId(trimmedDocId);
    setPetType(resolvedPetType || null);
    setModalPage(AUTH_GATE_PAGE);
    setIsPreparingPostAuthGeneration(true);
    setUploadEmailLinkPending(false);
    setAuthError('');
    setAuthInfo('');

    try {
      trackLandingEvent('fluffball-sign-in-succeeded', { source }, user);
      await beginGenerationForDoc(trimmedDocId, '', {
        waitForGenerationStart: false,
        petTypeOverride: resolvedPetType,
      });
      setModalPage(PET_NAME_PAGE);
    } catch (error) {
      console.error('Error starting generation after auth:', error);
      trackLandingEvent('fluffball-generation-start-failed', {
        source,
        doc_id: trimmedDocId,
        pet_type: resolvedPetType,
        error_message: String(error?.message || error),
      }, user);
      setAuthError(String(error?.message || error || 'Could not start your generation right now. Please try again.'));
      setModalPage(AUTH_GATE_PAGE);
    } finally {
      setIsPreparingPostAuthGeneration(false);
    }
  }, [beginGenerationForDoc, petType, trackLandingEvent]);

  const restorePendingLandingRedirect = useCallback(async (isMountedRef = { current: true }) => {
    const emailLinkResult = await consumePendingEmailLinkSignIn();
    if (!isMountedRef.current) {
      return;
    }

    if (emailLinkResult) {
      const source = getLandingEmailSource(emailLinkResult.flow);

      if (emailLinkResult.flow === 'landing_upload') {
        const restoredDocId = String(emailLinkResult.docId || '').trim();
        const restoredPetType = String(emailLinkResult.petType || '').trim();

        setGetStartedModalOpen(true);
        setUploadedDocId(restoredDocId);
        setPetType(restoredPetType || null);
        setModalPage(AUTH_GATE_PAGE);
        setIsPreparingPostAuthGeneration(false);

        if (emailLinkResult.status === 'success' && emailLinkResult.user) {
          setUploadEmailLinkPending(false);
          setAuthInfo('');
          setAuthError('');
          setIsCompletingFlowAuth(true);
          await finalizeUploadAuthAndStartGeneration({
            user: emailLinkResult.user,
            source,
            docIdValue: restoredDocId,
            petTypeOverride: restoredPetType,
          });
          if (isMountedRef.current) {
            setIsCompletingFlowAuth(false);
          }
          return;
        }

        setIsCompletingFlowAuth(false);

        if (emailLinkResult.status === 'awaiting_email') {
          setUploadEmailLinkPending(true);
          setAuthError('');
          setAuthInfo('Enter the email address you used for the sign-in link to finish signing in.');
          return;
        }

        setUploadEmailLinkPending(true);
        setAuthInfo('');
        if (emailLinkResult.status === 'error') {
          console.error('Upload email-link sign-in failed:', emailLinkResult.error);
          trackLandingEvent('fluffball-sign-in-failed', {
            source,
            error_message: String(emailLinkResult.error?.message || emailLinkResult.error),
          });
          setAuthError(String(emailLinkResult.error?.message || emailLinkResult.error || 'Could not sign you in right now. Please try again.'));
        }
        return;
      }

      if (emailLinkResult.flow === 'landing_header') {
        setHeaderAuthModalOpen(true);

        if (emailLinkResult.status === 'success' && emailLinkResult.user) {
          setHeaderEmailLinkPending(false);
          setHeaderAuthInfo('');
          setHeaderAuthError('');
          await handleHeaderAuthSuccess(emailLinkResult.user, source);
          if (isMountedRef.current) {
            setIsCompletingHeaderAuth(false);
          }
          return;
        }

        setIsCompletingHeaderAuth(false);

        if (emailLinkResult.status === 'awaiting_email') {
          setHeaderEmailLinkPending(true);
          setHeaderAuthError('');
          setHeaderAuthInfo('Enter the email address you used for the sign-in link to finish signing in.');
          return;
        }

        setHeaderEmailLinkPending(true);
        setHeaderAuthInfo('');
        if (emailLinkResult.status === 'error') {
          console.error('Header email-link sign-in failed:', emailLinkResult.error);
          trackLandingEvent('fluffball-sign-in-failed', {
            source,
            error_message: String(emailLinkResult.error?.message || emailLinkResult.error),
          });
          setHeaderAuthError(String(emailLinkResult.error?.message || emailLinkResult.error || 'Could not sign you in right now. Please try again.'));
        }
        return;
      }
    }

    const redirectResult = await consumePendingAuthRedirectResult();
    if (!isMountedRef.current || !redirectResult) {
      return;
    }

    const source = getLandingSocialSource(redirectResult.flow, redirectResult.providerId);

    if (redirectResult.flow === 'landing_upload') {
      const restoredDocId = String(redirectResult.docId || '').trim();
      const restoredPetType = String(redirectResult.petType || '').trim();

      setGetStartedModalOpen(true);
      setUploadedDocId(restoredDocId);
      setPetType(restoredPetType || null);
      setModalPage(AUTH_GATE_PAGE);
      setIsPreparingPostAuthGeneration(false);

      if (redirectResult.status === 'success' && redirectResult.user) {
        setIsCompletingFlowAuth(true);
        await finalizeUploadAuthAndStartGeneration({
          user: redirectResult.user,
          source,
          docIdValue: restoredDocId,
          petTypeOverride: restoredPetType,
        });
        if (isMountedRef.current) {
          setIsCompletingFlowAuth(false);
        }
        return;
      }

      if (redirectResult.status === 'error') {
        console.error('Upload redirect sign-in failed:', redirectResult.error);
        trackLandingEvent('fluffball-sign-in-failed', {
          source,
          error_message: String(redirectResult.error?.message || redirectResult.error),
        });
        setAuthError(String(redirectResult.error?.message || redirectResult.error || 'Could not sign you in right now. Please try again.'));
      } else if (redirectResult.status === 'cancelled') {
        setAuthError('Sign-in was cancelled. Please try again.');
      }

      setIsCompletingFlowAuth(false);
      return;
    }

    if (redirectResult.flow !== 'landing_header') {
      return;
    }

    if (redirectResult.status === 'success' && redirectResult.user) {
      await handleHeaderAuthSuccess(redirectResult.user, source);
      if (isMountedRef.current) {
        setIsCompletingHeaderAuth(false);
      }
      return;
    }

    setHeaderAuthModalOpen(true);
    if (redirectResult.status === 'error') {
      console.error('Header redirect sign-in failed:', redirectResult.error);
      trackLandingEvent('fluffball-sign-in-failed', {
        source,
        error_message: String(redirectResult.error?.message || redirectResult.error),
      });
      setHeaderAuthError(String(redirectResult.error?.message || redirectResult.error || 'Could not sign you in right now. Please try again.'));
    } else if (redirectResult.status === 'cancelled') {
      setHeaderAuthError('Sign-in was cancelled. Please try again.');
    }
    setIsCompletingHeaderAuth(false);
  }, [finalizeUploadAuthAndStartGeneration, handleHeaderAuthSuccess, trackLandingEvent]);

  useEffect(() => {
    const isMountedRef = { current: true };
    const runRestore = () => {
      void restorePendingLandingRedirect(isMountedRef);
    };

    if (!authRedirectHandledRef.current) {
      authRedirectHandledRef.current = true;
      runRestore();
    }

    const handlePageShow = () => {
      runRestore();
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [restorePendingLandingRedirect]);

  const handleHeaderGoogleAuth = async () => {
    if (isCompletingHeaderAuth) {
      return;
    }

    const source = 'home_header_sign_in_google';
    setIsCompletingHeaderAuth(true);
    setHeaderAuthError('');
    setHeaderAuthInfo('');
    setHeaderEmailLinkPending(false);
    trackLandingEvent('fluffball-sign-in-clicked', { source });

    try {
      const user = await completeFlowSignInWithGoogle({
        redirectFlow: 'landing_header',
      });
      if (!user) {
        return;
      }
      await handleHeaderAuthSuccess(user, source);
    } catch (error) {
      console.error('Header Google sign-in failed:', error);
      trackLandingEvent('fluffball-sign-in-failed', {
        source,
        error_message: String(error?.message || error),
      });
      setHeaderAuthError(String(error?.message || error || 'Could not sign you in right now. Please try again.'));
    } finally {
      setIsCompletingHeaderAuth(false);
    }
  };

  const handleHeaderAppleAuth = async () => {
    if (isCompletingHeaderAuth) {
      return;
    }

    const source = 'home_header_sign_in_apple';
    setIsCompletingHeaderAuth(true);
    setHeaderAuthError('');
    setHeaderAuthInfo('');
    setHeaderEmailLinkPending(false);
    trackLandingEvent('fluffball-sign-in-clicked', { source });

    try {
      const user = await completeFlowSignInWithApple({
        redirectFlow: 'landing_header',
      });
      if (!user) {
        return;
      }
      await handleHeaderAuthSuccess(user, source);
    } catch (error) {
      console.error('Header Apple sign-in failed:', error);
      trackLandingEvent('fluffball-sign-in-failed', {
        source,
        error_message: String(error?.message || error),
      });
      setHeaderAuthError(String(error?.message || error || 'Could not sign you in right now. Please try again.'));
    } finally {
      setIsCompletingHeaderAuth(false);
    }
  };

  const handleHeaderEmailAuth = async ({ email, password, mode }) => {
    if (isCompletingHeaderAuth) {
      return;
    }

    const source = mode === 'sign_in' ? 'home_header_email_sign_in' : 'home_header_email_create';
    setIsCompletingHeaderAuth(true);
    setHeaderAuthError('');
    setHeaderAuthInfo('');
    setHeaderEmailLinkPending(false);
    trackLandingEvent('fluffball-sign-in-clicked', { source });

    try {
      const user = await completeFlowEmailPasswordAuth({
        email,
        password,
        mode,
      });
      await handleHeaderAuthSuccess(user, source);
    } catch (error) {
      console.error('Header email auth failed:', error);
      trackLandingEvent('fluffball-sign-in-failed', {
        source,
        error_message: String(error?.message || error),
      });
      setHeaderAuthError(String(error?.message || error || 'Could not sign you in right now. Please try again.'));
    } finally {
      setIsCompletingHeaderAuth(false);
    }
  };

  const handleHeaderEmailCreate = ({ email, password }) => handleHeaderEmailAuth({
    email,
    password,
    mode: 'create',
  });

  const handleHeaderEmailSignIn = ({ email, password }) => handleHeaderEmailAuth({
    email,
    password,
    mode: 'sign_in',
  });

  const handleHeaderEmailLinkSend = async ({ email }) => {
    if (isCompletingHeaderAuth) {
      return;
    }

    const source = 'home_header_email_link';
    setIsCompletingHeaderAuth(true);
    setHeaderAuthError('');
    setHeaderAuthInfo('');
    setHeaderEmailLinkPending(false);
    trackLandingEvent('fluffball-sign-in-clicked', { source });

    try {
      await sendEmailSignInLinkForFlow({
        email,
        flow: 'landing_header',
      });
      setHeaderAuthInfo('Check your email for a sign-in link. If it opens in another browser, enter the same email here to finish.');
    } catch (error) {
      console.error('Header email-link sign-in failed:', error);
      trackLandingEvent('fluffball-sign-in-failed', {
        source,
        error_message: String(error?.message || error),
      });
      setHeaderAuthError(String(error?.message || error || 'Could not email you a sign-in link right now. Please try again.'));
    } finally {
      setIsCompletingHeaderAuth(false);
    }
  };

  const handleHeaderEmailLinkComplete = async ({ email }) => {
    if (isCompletingHeaderAuth) {
      return;
    }

    const source = 'home_header_email_link';
    setIsCompletingHeaderAuth(true);
    setHeaderAuthError('');
    setHeaderAuthInfo('');
    setHeaderEmailLinkPending(true);
    trackLandingEvent('fluffball-sign-in-clicked', { source });

    try {
      const result = await completePendingEmailLinkSignIn({ email });
      if (result?.status !== 'success' || !result.user) {
        throw result?.error || new Error('This sign-in link is invalid or has already been used.');
      }
      setHeaderEmailLinkPending(false);
      await handleHeaderAuthSuccess(result.user, source);
    } catch (error) {
      console.error('Header email-link completion failed:', error);
      trackLandingEvent('fluffball-sign-in-failed', {
        source,
        error_message: String(error?.message || error),
      });
      setHeaderAuthError(String(error?.message || error || 'Could not sign you in right now. Please try again.'));
    } finally {
      setIsCompletingHeaderAuth(false);
    }
  };

  const primaryHeroButtonLabel = isStartingDailyVideo
    ? 'Starting...'
    : (isIOSMobile ? 'Get the app' : 'Try it with my pet');
  const primaryHeroSubtext = '';

  let getStartedModalContent;
  const questionnaireSetters = {
    petPersonality: setPetPersonality,
    petAgeBucket: setPetAgeBucket,
    petDescriptor: setPetDescriptor,
    petFavoriteTrait: setPetFavoriteTrait,
    petFavoriteActivity: setPetFavoriteActivity,
    petSocialStyle: setPetSocialStyle,
    petEnergyLevel: setPetEnergyLevel,
    petHappyPlace: setPetHappyPlace,
    petAffectionLevel: setPetAffectionLevel,
    petConfidenceStyle: setPetConfidenceStyle,
    petVocalStyle: setPetVocalStyle,
    petTreatMotivation: setPetTreatMotivation,
    petPlayStyle: setPetPlayStyle,
    petMischiefLevel: setPetMischiefLevel,
    petRoutineStyle: setPetRoutineStyle,
  };
  const questionnaireValues = {
    petPersonality,
    petAgeBucket,
    petDescriptor,
    petFavoriteTrait,
    petFavoriteActivity,
    petSocialStyle,
    petEnergyLevel,
    petHappyPlace,
    petAffectionLevel,
    petConfidenceStyle,
    petVocalStyle,
    petTreatMotivation,
    petPlayStyle,
    petMischiefLevel,
    petRoutineStyle,
  };
  // copied from holiday card
  // if (modalPage == 1) {

  //   const onSubmit = (people) => {
  //     setModalPage(2)
  //     setPeople(people)
  //     console.log(people)
  //   }
  //   getStartedModalContent = <AddPeople onSubmit={onSubmit}/>

  // }
  if (modalPage === 1) {
    const onSubmitPetType = async (petType) => {
      setAuthError('');
      trackLandingEvent('fluffball-pet-type-selected', {
        pet_type: petType,
      });
      const draftCreationToken = pendingDraftCreationTokenRef.current + 1;
      pendingDraftCreationTokenRef.current = draftCreationToken;
      setPetType(petType);
      setPetName('');
      setPetPersonality('');
      setPetAgeBucket('');
      setPetDescriptor('');
      setPetFavoriteTrait('');
      setPetFavoriteActivity('');
      setPetSocialStyle('');
      setPetEnergyLevel('');
      setPetHappyPlace('');
      setPetAffectionLevel('');
      setPetConfidenceStyle('');
      setPetVocalStyle('');
      setPetTreatMotivation('');
      setPetPlayStyle('');
      setPetMischiefLevel('');
      setPetRoutineStyle('');
      setUploadedDocId('');
      setModalPage(2);

      void createDraftPetPhotoDoc({
        petType,
        petName: '',
      })
        .then((draftDocId) => {
          if (pendingDraftCreationTokenRef.current !== draftCreationToken) {
            return;
          }
          setUploadedDocId((currentDocId) => currentDocId || draftDocId);
        })
        .catch((error) => {
          if (pendingDraftCreationTokenRef.current !== draftCreationToken) {
            return;
          }
          console.error('Failed to create draft upload doc in background:', error);
        });
    }
    getStartedModalContent = <PetSelector 
      onSubmit={onSubmitPetType}
    />

  } else if (modalPage === 2) {
    const handleUpload = (familyMembers, photosMap) => onUploadPhotos(
      familyMembers,
      photosMap,
      {
        docId: uploadedDocId,
        petType,
        petName: '',
      }
    );

    const onSubmit = async (docId) => {
      const trimmedDocId = String(docId || '').trim();
      try {
        setAuthError('');
        setUploadedDocId(trimmedDocId);

        const currentUser = getCurrentUser();
        if (!currentUser || currentUser.isAnonymous) {
          setModalPage(AUTH_GATE_PAGE);
          return;
        }

        await beginGenerationForDoc(trimmedDocId, '', { waitForGenerationStart: false });
        setModalPage(PET_NAME_PAGE);
      } catch (error) {
        console.error("Error starting generation flow:", error);
        trackLandingEvent('fluffball-generation-start-failed', {
          doc_id: trimmedDocId,
          pet_type: petType || '',
          pet_name: '',
          error_message: String(error?.message || error),
        });
        throw error;
      }
    };
    getStartedModalContent = <PhotoUploader 
      familyMembers={[{name:'YourPet'}]}
      docId={uploadedDocId}
      petType={petType}
      onUpload={handleUpload}
      onSubmit={onSubmit}
    />
  } else if (isTransitioningToResult) {
    getStartedModalContent = (
      <div className="min-h-[26rem] flex items-center justify-center px-6 py-12">
        <TailSpin stroke="#8B5CF6" speed={0.75} width="48" />
      </div>
    );
  } else if (isPreparingPostAuthGeneration) {
    getStartedModalContent = (
      <div className="min-h-[26rem] flex items-center justify-center px-6 py-12">
        <TailSpin stroke="#8B5CF6" speed={0.75} width="48" />
      </div>
    );
  } else if (modalPage === AUTH_GATE_PAGE) {
    const trimmedDocId = String(uploadedDocId || '').trim();

    const handleGoogleAuth = async () => {
      if (!trimmedDocId || isCompletingFlowAuth) {
        return;
      }

      const source = 'upload_continue_google';
      setIsCompletingFlowAuth(true);
      setAuthError('');
      setAuthInfo('');
      setUploadEmailLinkPending(false);
      trackLandingEvent('fluffball-sign-in-clicked', { source });

      try {
        const signedInUser = await completeFlowSignInWithGoogle({
          docId: trimmedDocId,
          redirectFlow: 'landing_upload',
          petType,
        });
        if (!signedInUser) {
          return;
        }
        await finalizeUploadAuthAndStartGeneration({
          user: signedInUser,
          source,
          docIdValue: trimmedDocId,
          petTypeOverride: petType,
        });
      } catch (error) {
        console.error('Google auth gate sign-in failed:', error);
        trackLandingEvent('fluffball-sign-in-failed', {
          source,
          error_message: String(error?.message || error),
        });
        setAuthError(String(error?.message || error || 'Could not sign you in right now. Please try again.'));
      } finally {
        setIsCompletingFlowAuth(false);
      }
    };

    const handleAppleAuth = async () => {
      if (!trimmedDocId || isCompletingFlowAuth) {
        return;
      }

      const source = 'upload_continue_apple';
      setIsCompletingFlowAuth(true);
      setAuthError('');
      setAuthInfo('');
      setUploadEmailLinkPending(false);
      trackLandingEvent('fluffball-sign-in-clicked', { source });

      try {
        const signedInUser = await completeFlowSignInWithApple({
          docId: trimmedDocId,
          redirectFlow: 'landing_upload',
          petType,
        });
        if (!signedInUser) {
          return;
        }
        await finalizeUploadAuthAndStartGeneration({
          user: signedInUser,
          source,
          docIdValue: trimmedDocId,
          petTypeOverride: petType,
        });
      } catch (error) {
        console.error('Apple auth gate sign-in failed:', error);
        trackLandingEvent('fluffball-sign-in-failed', {
          source,
          error_message: String(error?.message || error),
        });
        setAuthError(String(error?.message || error || 'Could not sign you in right now. Please try again.'));
      } finally {
        setIsCompletingFlowAuth(false);
      }
    };

    const handleEmailAuth = async ({ email, password, mode }) => {
      if (!trimmedDocId || isCompletingFlowAuth) {
        return;
      }

      const source = mode === 'sign_in' ? 'upload_continue_email_sign_in' : 'upload_continue_email_create';
      setIsCompletingFlowAuth(true);
      setAuthError('');
      setAuthInfo('');
      setUploadEmailLinkPending(false);
      trackLandingEvent('fluffball-sign-in-clicked', { source });

      try {
        const signedInUser = await completeFlowEmailPasswordAuth({
          docId: trimmedDocId,
          email,
          password,
          mode,
        });
        await finalizeUploadAuthAndStartGeneration({
          user: signedInUser,
          source,
          docIdValue: trimmedDocId,
          petTypeOverride: petType,
        });
      } catch (error) {
        console.error('Email auth gate flow failed:', error);
        trackLandingEvent('fluffball-sign-in-failed', {
          source,
          error_message: String(error?.message || error),
        });
        setAuthError(String(error?.message || error || 'Could not sign you in right now. Please try again.'));
      } finally {
        setIsCompletingFlowAuth(false);
      }
    };

    const handleEmailCreate = ({ email, password }) => handleEmailAuth({
      email,
      password,
      mode: 'create',
    });

    const handleEmailSignIn = ({ email, password }) => handleEmailAuth({
      email,
      password,
      mode: 'sign_in',
    });

    const handleEmailLinkSend = async ({ email }) => {
      if (!trimmedDocId || isCompletingFlowAuth) {
        return;
      }

      const source = 'upload_continue_email_link';
      setIsCompletingFlowAuth(true);
      setAuthError('');
      setAuthInfo('');
      setUploadEmailLinkPending(false);
      trackLandingEvent('fluffball-sign-in-clicked', { source });

      try {
        await sendEmailSignInLinkForFlow({
          email,
          flow: 'landing_upload',
          docId: trimmedDocId,
          petType,
        });
        setAuthInfo('Check your email for a sign-in link. If it opens in another browser, enter the same email here to finish.');
      } catch (error) {
        console.error('Upload email-link sign-in failed:', error);
        trackLandingEvent('fluffball-sign-in-failed', {
          source,
          error_message: String(error?.message || error),
        });
        setAuthError(String(error?.message || error || 'Could not email you a sign-in link right now. Please try again.'));
      } finally {
        setIsCompletingFlowAuth(false);
      }
    };

    const handleEmailLinkComplete = async ({ email }) => {
      if (!trimmedDocId || isCompletingFlowAuth) {
        return;
      }

      const source = 'upload_continue_email_link';
      setIsCompletingFlowAuth(true);
      setAuthError('');
      setAuthInfo('');
      setUploadEmailLinkPending(true);
      trackLandingEvent('fluffball-sign-in-clicked', { source });

      try {
        const result = await completePendingEmailLinkSignIn({ email });
        if (result?.status !== 'success' || !result.user) {
          throw result?.error || new Error('This sign-in link is invalid or has already been used.');
        }
        setUploadEmailLinkPending(false);
        await finalizeUploadAuthAndStartGeneration({
          user: result.user,
          source,
          docIdValue: trimmedDocId,
          petTypeOverride: petType,
        });
      } catch (error) {
        console.error('Upload email-link completion failed:', error);
        trackLandingEvent('fluffball-sign-in-failed', {
          source,
          error_message: String(error?.message || error),
        });
        setAuthError(String(error?.message || error || 'Could not sign you in right now. Please try again.'));
      } finally {
        setIsCompletingFlowAuth(false);
      }
    };

    getStartedModalContent = (
      <AuthGateStep
        isLoading={isCompletingFlowAuth}
        error={authError}
        info={authInfo}
        showSocialAuth={!prefersMagicLinkEmailAuth}
        subtitle={
          prefersMagicLinkEmailAuth
            ? 'Sign in with email to continue. We\'ll send you a secure sign-in link.'
            : 'Sign in with Apple, Google, or email to start making your pet\'s baby videos.'
        }
        emailAuthMode={
          uploadEmailLinkPending
            ? 'magic_link_complete'
            : (prefersMagicLinkEmailAuth ? 'magic_link_send' : 'password')
        }
        onApple={handleAppleAuth}
        onGoogle={handleGoogleAuth}
        onEmailCreate={handleEmailCreate}
        onEmailSignIn={handleEmailSignIn}
        onEmailLinkSend={handleEmailLinkSend}
        onEmailLinkComplete={handleEmailLinkComplete}
        onClearError={() => {
          setAuthError('');
          setAuthInfo('');
        }}
      />
    );
  } else if (modalPage === PET_NAME_PAGE) {
    const onSubmitPetName = (name) => {
      const trimmedDocId = String(uploadedDocId || '').trim();
      if (!trimmedDocId) {
        setAuthError("Please upload your pet photos before continuing.");
        setModalPage(2);
        return;
      }

      trackLandingEvent('fluffball-pet-name-submitted', {
        pet_type: petType || '',
        pet_name: name,
      });
      setPetName(name);
      updatePetQuestionnaireInFirebase(trimmedDocId, { petName: name }).catch((error) => {
        console.error('Failed to save pet name:', error);
      });
      setModalPage(QUESTION_FLOW_START_PAGE);
    };

    getStartedModalContent = (
      <PetNameInput
        initialName={petName}
        onSubmit={onSubmitPetName}
        onBack={() => {
          setAuthError('');
          setModalPage(2);
        }}
      />
    );
  } else if (
    modalPage >= QUESTION_FLOW_START_PAGE
    && modalPage < QUESTION_FLOW_START_PAGE + QUESTIONNAIRE_STEPS.length
  ) {
    const petDisplayName = getPetDisplayName(petName);
    const stepIndex = modalPage - QUESTION_FLOW_START_PAGE;
    const stepConfig = QUESTIONNAIRE_STEPS[stepIndex];
    const currentQuestionValue = questionnaireValues[stepConfig.key] || '';
    const onSelectQuestionValue = async (value) => {
      const trimmedDocId = String(uploadedDocId || '').trim();
      if (!trimmedDocId) {
        setAuthError("Please upload your pet photos before continuing.");
        setModalPage(2);
        return;
      }

      trackLandingEvent(stepConfig.analyticsEvent, {
        doc_id: trimmedDocId,
        pet_type: petType || '',
        pet_name: petName || '',
        [stepConfig.analyticsProperty]: value,
      });
      questionnaireSetters[stepConfig.key](value);
      const persistAnswerPromise = updatePetQuestionnaireInFirebase(trimmedDocId, {
        [stepConfig.key]: value,
      }).catch((error) => {
        console.error(`Failed to save ${stepConfig.key}:`, error);
      });

      if (stepIndex >= QUESTIONNAIRE_STEPS.length - 1) {
        setIsTransitioningToResult(true);
        try {
          await persistAnswerPromise;
          const initialResultData = await waitForFirstResultSnapshot(trimmedDocId);
          navigate(getModernResultPath(trimmedDocId), {
            state: {
              initialResultDocId: trimmedDocId,
              initialResultData,
            },
          });
        } catch (transitionError) {
          console.error('Failed to transition to result page:', transitionError);
          setIsTransitioningToResult(false);
          navigate(getModernResultPath(trimmedDocId));
        }
        return;
      }

      setModalPage(modalPage + 1);
    };
    const handleQuestionBack = () => {
      setAuthError('');
      if (stepIndex > 0) {
        setModalPage(modalPage - 1);
        return;
      }
      setModalPage(PET_NAME_PAGE);
    };

    getStartedModalContent = (
      <PetChoiceQuestion
        key={`questionnaire-step-${stepConfig.key}`}
        title={stepConfig.title(petDisplayName)}
        options={stepConfig.options}
        initialValue={currentQuestionValue}
        onSelect={onSelectQuestionValue}
        onBack={handleQuestionBack}
      />
    );
  }

  // Decorative elements
  const PawPrint = ({ className }) => (
    <div className={`absolute opacity-20 ${className}`}>
      <svg width="40" height="40" viewBox="0 0 100 100" fill="currentColor">
        <path d="M57.9,44.4c-7.4,2.5-12.4,9-11.3,14.7c1.2,5.7,8.1,8.3,15.5,5.8c7.4-2.5,12.4-9,11.3-14.7C72.2,44.5,65.3,41.9,57.9,44.4z"/>
        <path d="M37.9,35.8c4.8-6.3,4.8-13.9,0-16.9c-4.8-3-12.5-0.3-17.3,6c-4.8,6.3-4.8,13.9,0,16.9C25.3,44.8,33.1,42.1,37.9,35.8z"/>
        <path d="M75.9,18.3c-5-6.1-12.9-8.5-17.6-5.3c-4.7,3.2-4.4,10.8,0.6,16.9c5,6.1,12.9,8.5,17.6,5.3C81.2,32,80.9,24.4,75.9,18.3z"/>
        <path d="M24.8,61.5c-8.1,0.6-14.5,5.8-14.2,11.5c0.3,5.7,7,9.9,15.1,9.3c8.1-0.6,14.5-5.8,14.2-11.5C39.6,65.1,32.9,60.9,24.8,61.5z"/>
      </svg>
    </div>
  );

  return (
    <div className="bg-gradient-to-b from-blue-100 to-purple-100 text-gray-700 min-h-screen flex flex-col relative overflow-hidden pb-40 sm:pb-0">
      {/* Decorative paw prints */}
      <PawPrint className="text-pink-300 top-20 left-10 transform rotate-12" />
      <PawPrint className="text-blue-300 top-40 right-10 transform -rotate-12" />
      <PawPrint className="text-yellow-300 bottom-40 left-20 transform rotate-45" />
      <PawPrint className="text-green-300 bottom-20 right-20 transform -rotate-45" />
      
      {/* Get Started Modal component with its content */}
      <Modal isOpen={getStartedModalOpen} handleClose={handleCloseGetStartedModal}>
        <div className="overflow-x-hidden">
          {getStartedModalContent}
        </div>
      </Modal>

      <Modal isOpen={headerAuthModalOpen} handleClose={handleCloseHeaderAuthModal}>
        <div className="overflow-x-hidden">
          <AuthGateStep
            key="header-auth"
            initialMode="sign_in"
            signInTitle="Sign in"
            subtitle={
              prefersMagicLinkEmailAuth
                ? 'Sign in with email to access your Fluffball account. We\'ll send you a secure sign-in link.'
                : 'Use Apple, Google, or email to access your Fluffball account.'
            }
            isLoading={isCompletingHeaderAuth}
            error={headerAuthError}
            info={headerAuthInfo}
            showSocialAuth={!prefersMagicLinkEmailAuth}
            emailAuthMode={
              headerEmailLinkPending
                ? 'magic_link_complete'
                : (prefersMagicLinkEmailAuth ? 'magic_link_send' : 'password')
            }
            onApple={handleHeaderAppleAuth}
            onGoogle={handleHeaderGoogleAuth}
            onEmailCreate={handleHeaderEmailCreate}
            onEmailSignIn={handleHeaderEmailSignIn}
            onEmailLinkSend={handleHeaderEmailLinkSend}
            onEmailLinkComplete={handleHeaderEmailLinkComplete}
            onClearError={() => {
              setHeaderAuthError('');
              setHeaderAuthInfo('');
            }}
          />
        </div>
      </Modal>
      
      <AppHeader>
        {headerIsSignedIn ? (
          <>
            {headerCanAccessProfile ? (
              <Link to="/profile" className={appHeaderActionClasses}>Profile</Link>
            ) : null}
            <button
              className={appHeaderActionClasses}
              onClick={handleSignOut}
            >
              Sign out
            </button>
          </>
        ) : (
          isIOSMobile ? (
            <button
              className={appHeaderActionClasses}
              onClick={() => {
                handleAppStoreClick('header');
              }}
            >
              Get the app
            </button>
          ) : (
            <button
              className={appHeaderActionClasses}
              onClick={handleHeaderSignIn}
              disabled={isCompletingHeaderAuth}
            >
              {isCompletingHeaderAuth ? 'Signing in...' : 'Sign in'}
            </button>
          )
        )}
      </AppHeader>

      {/* Main Content */}
      <main className="flex-grow flex flex-col items-center max-w-7xl mx-auto z-10 relative w-full px-5 pt-6 pb-6 sm:px-8 sm:pt-12 sm:pb-10 lg:px-10">
        <div className="w-full max-w-5xl flex flex-col items-center">
          {/* Headline */}
          <h1 className="text-center text-[2.3rem] font-extrabold leading-[1.04] tracking-tight text-slate-800 sm:text-5xl xl:text-6xl">
            Meet the puppy or kitten
            <span className="mt-1 block bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
              you never met
            </span>
          </h1>

          <p className="mt-4 max-w-md text-center text-base leading-relaxed text-slate-600 sm:mt-5 sm:max-w-xl sm:text-lg">
            See your rescue as a newborn — then raise them day by day.
          </p>

          {/* Hero media: real "before" photo → newborn-to-grown video, framed in a phone */}
          <div className="relative mt-9 flex w-full justify-center sm:mt-11">
            <div className="relative w-[212px] rounded-[2.4rem] border-[7px] border-slate-900 bg-slate-900 shadow-2xl sm:w-[248px]">
              <div className="relative overflow-hidden rounded-[1.85rem]">
                <LoopVideo
                  src="/app/grow.mp4"
                  poster="/app/grow.jpg"
                  className="aspect-[9/16] w-full object-cover"
                />

                {/* Source photo inset — the "before" */}
                <div className="absolute left-2.5 top-2.5 w-[36%]">
                  <div className="overflow-hidden rounded-xl border-2 border-white shadow-md">
                    <img
                      src="/app/before-dog.jpg"
                      alt="An adult rescue dog"
                      className="aspect-square w-full object-cover"
                      width={480}
                      height={480}
                      loading="eager"
                    />
                  </div>
                  <span className="mx-auto mt-1 block w-fit rounded-full bg-white/90 px-2 py-0.5 text-[0.55rem] font-semibold text-slate-600 shadow-sm sm:text-[0.65rem]">
                    Your rescue
                  </span>
                </div>

                <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-black/45 px-3 py-1 text-[0.65rem] font-medium text-white backdrop-blur sm:text-xs">
                  Reimagined as a newborn 🍼
                </div>
              </div>
            </div>
          </div>

          {/* Primary CTA — on mobile the fixed bottom bar handles this, so show only on sm+ */}
          <div className="mt-8 hidden w-full max-w-sm flex-col items-center sm:mt-10 sm:flex">
            <button
              onClick={() => handlePrimaryHeroAction('hero')}
              disabled={isStartingDailyVideo}
              className="shine-effect relative w-full cursor-pointer overflow-hidden rounded-full bg-gradient-to-r from-pink-500 to-purple-500 px-8 py-4 text-xl font-bold text-white shadow-lg transition-all duration-300 hover:from-pink-600 hover:to-purple-600 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-pink-300 focus:ring-opacity-50 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:scale-100 sm:text-2xl sm:py-5"
            >
              {primaryHeroButtonLabel}
            </button>
            {primaryHeroSubtext ? (
              <p className="mt-3 text-center text-sm text-slate-500">{primaryHeroSubtext}</p>
            ) : null}
            {authError ? (
              <p className="mt-3 text-center text-sm text-red-600">{authError}</p>
            ) : null}
            {homeStatusMessage ? (
              <p className="mt-2 text-center text-sm text-red-600">{homeStatusMessage}</p>
            ) : null}
          </div>
        </div>
      </main>

      {/* Watch them grow — the daily care loop */}
      <section className="px-5 py-12 sm:py-16">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center text-2xl font-bold text-slate-800 sm:text-4xl">
            Watch them grow up, day by day
          </h2>
          <p className="mx-auto mt-3 max-w-md text-center text-base text-slate-600 sm:max-w-xl sm:text-lg">
            Feed them, rock them to sleep, cheer on every milestone. Each day, they grow a little more.
          </p>
          <div className="mt-9 grid grid-cols-3 gap-3 sm:gap-6">
            {GROWTH_STAGES.map((stage) => (
              <div key={stage.src} className="flex flex-col items-center">
                <div className="w-full overflow-hidden rounded-2xl border-4 border-white bg-pink-50 shadow-lg">
                  <LoopVideo
                    src={stage.src}
                    poster={stage.poster}
                    className="aspect-[3/4] w-full object-cover"
                  />
                </div>
                <p className="mt-3 text-sm font-bold text-purple-600 sm:text-base">{stage.day}</p>
                <p className="px-1 text-center text-xs leading-tight text-slate-500 sm:text-sm">{stage.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className='bg-white bg-opacity-80 py-10 mt-12'>
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <InstagramWall posts={instagramPosts} linkToSeeMore={"https://www.instagram.com/stories/highlights/17918144279824183/?hl=en"}/>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="py-16 px-4 bg-gradient-to-r from-purple-100 to-pink-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-purple-600">How It Works</h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-6 shadow-md text-center transform transition-all duration-300 hover:shadow-xl">
              <div className="bg-pink-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">📸</span>
              </div>
              <h3 className="text-xl font-semibold mb-2 text-pink-500">Add your pet</h3>
              <p className="text-gray-600">Snap a few photos of your rescue — or any pet you love.</p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-md text-center transform transition-all duration-300 hover:shadow-xl">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🍼</span>
              </div>
              <h3 className="text-xl font-semibold mb-2 text-purple-500">Meet them as a newborn</h3>
              <p className="text-gray-600">Fluffball reimagines them as the tiny puppy or kitten you never got to meet.</p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-md text-center transform transition-all duration-300 hover:shadow-xl">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🐾</span>
              </div>
              <h3 className="text-xl font-semibold mb-2 text-blue-500">Raise them, day by day</h3>
              <p className="text-gray-600">Feed, snuggle, and watch them grow into the pet you know and love.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="text-center p-8 bg-gradient-to-r from-purple-500 to-pink-500 text-white">
        <div className="max-w-4xl mx-auto">
          <p className="text-lg font-medium mb-4">
            See the baby they were — and raise them all over again ❤️
          </p>
          <div className="hidden sm:flex justify-center">
            <button 
              onClick={() => handlePrimaryHeroAction('footer')}
              disabled={isStartingDailyVideo}
              className="shine-effect cursor-pointer text-lg px-6 py-3 bg-white text-pink-500 font-bold rounded-full shadow-lg hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 mb-6 relative overflow-hidden disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:scale-100"
            >
              {primaryHeroButtonLabel}
            </button>
          </div>
          <p className="text-sm mt-4">
            Made with ❤️ for rescue pets everywhere
          </p>
          <LegalFooter variant="inverse" className="mt-8" />
        </div>
      </footer>

      <div
        className="fixed inset-x-0 bottom-0 z-30 border-t border-pink-200/80 bg-gradient-to-r from-pink-100/95 via-purple-100/95 to-blue-100/95 px-4 pt-3 shadow-2xl backdrop-blur sm:hidden"
        style={{
          paddingBottom: isIOSMobile
            ? 'calc(env(safe-area-inset-bottom) + 1.75rem)'
            : 'calc(env(safe-area-inset-bottom) + 0.9rem)',
        }}
      >
        <div className="mx-auto flex max-w-lg flex-col items-center gap-3">
          <div className="flex items-center justify-center gap-2 text-sm font-medium text-slate-700">
            <span aria-hidden="true" className="text-base text-pink-500">✓</span>
            <span>Loved by thousands of rescue parents</span>
          </div>
          <button
            onClick={() => handleAppStoreClick('sticky_mobile')}
            className="shine-effect w-full cursor-pointer rounded-full bg-gradient-to-r from-pink-500 to-purple-500 px-6 py-4 text-lg font-bold text-white shadow-lg transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-pink-300 focus:ring-opacity-50"
          >
            Get the app
          </button>
        </div>
      </div>

      {/* Add the custom sheen effect animation to the stylesheet */}
      <style>{`
        @keyframes sheen {
          0% {
            transform: translateX(-100%) skewX(-15deg);
          }
          100% {
            transform: translateX(200%) skewX(-15deg);
          }
        }
        
        .shine-effect {
          position: relative;
          overflow: hidden;
        }
        
        .shine-effect::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            to right, 
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.4) 50%,
            rgba(255, 255, 255, 0) 100%
          );
          transform: translateX(-100%) skewX(-15deg);
          animation: sheen 6s infinite;
          animation-delay: 2s;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
